import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { matchesQuery } from '../lib/search.js';
import { formatQty } from '../lib/format.js';
import { useAuth } from '../lib/auth-context.js';
import { type SortDir, useTableSort } from '../lib/sortRows.js';
import { MoveShelfToPalletDialog } from '../components/MoveShelfToPalletDialog.js';
import { ThumbButton } from '../components/ThumbButton.js';

const { t } = i18n;

interface StockRow {
  skuId: string;
  code: string;
  nameLt: string;
  nameEn: string | null;
  photoUrl: string | null;
  partnerId: string;
  partnerCode: string;
  partnerName: string;
  onHand: number;
  stockPallets: { qty: number }[];
}

interface DashboardResponse {
  components: StockRow[];
  products: StockRow[];
}

interface ShelfRow extends StockRow {
  type: 'COMPONENT' | 'PRODUCT';
  shelfQty: number;
}

const SHELF_SORT = {
  partner: (row: ShelfRow) => row.partnerCode,
  type: (row: ShelfRow) => t(`sku.type.${row.type}`),
  code: (row: ShelfRow) => row.code,
  name: (row: ShelfRow) => row.nameLt,
  qty: (row: ShelfRow) => row.shelfQty,
} as const;

interface SortCtl {
  sortKey: string;
  sortDir: SortDir;
  toggle: (key: string) => void;
}

function SortableTh({
  label,
  col,
  sort,
  align,
}: {
  label: string;
  col: string;
  sort: SortCtl;
  align?: 'right';
}) {
  const active = sort.sortKey === col;
  return (
    <th
      className={`px-3 py-2 ${align === 'right' ? 'text-right' : ''}`}
      aria-sort={active ? (sort.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => sort.toggle(col)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${align === 'right' ? 'flex-row-reverse' : ''}`}
        aria-label={label}
      >
        <span>{label}</span>
        <span className="text-[10px] text-muted-foreground" aria-hidden="true">
          {active ? (sort.sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

/** Virtual Shelf stock is the posted balance not currently held on pallets. */
export function shelfRows(data: DashboardResponse): ShelfRow[] {
  const rows: (StockRow & { type: ShelfRow['type'] })[] = [
    ...data.components.map((row) => ({ ...row, type: 'COMPONENT' as const })),
    ...data.products.map((row) => ({ ...row, type: 'PRODUCT' as const })),
  ];
  return rows
    .map((row) => ({
      ...row,
      shelfQty: row.onHand - row.stockPallets.reduce((sum, pallet) => sum + pallet.qty, 0),
    }))
    .filter((row) => row.shelfQty > 0)
    .sort((a, b) => a.partnerCode.localeCompare(b.partnerCode) || a.code.localeCompare(b.code));
}

export function ShelfPage() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [selected, setSelected] = useState<ShelfRow | null>(null);

  const canMove = Boolean(
    user?.roles.some((binding) => ['ADMIN', 'OPERATOR'].includes(binding.role)),
  );

  function reload() {
    api<DashboardResponse>('/stock/dashboard')
      .then(setData)
      .catch((err: unknown) => setError(String(err)));
  }

  useEffect(() => {
    api<DashboardResponse>('/stock/dashboard')
      .then(setData)
      .catch((err: unknown) => setError(String(err)));
  }, []);

  const shelfDataRows = useMemo(() => (data ? shelfRows(data) : []), [data]);
  const partners = useMemo(() => {
    const byId = new Map<string, { id: string; code: string; name: string }>();
    for (const row of shelfDataRows) {
      if (!byId.has(row.partnerId)) {
        byId.set(row.partnerId, {
          id: row.partnerId,
          code: row.partnerCode,
          name: row.partnerName,
        });
      }
    }
    return [...byId.values()].sort((a, b) => a.code.localeCompare(b.code, 'lt'));
  }, [shelfDataRows]);
  const rows = useMemo(
    () =>
      shelfDataRows.filter(
        (row) =>
          (!partnerFilter || row.partnerId === partnerFilter) &&
          matchesQuery(search, row.code, row.nameLt, row.nameEn, row.partnerCode, row.partnerName),
      ),
    [shelfDataRows, partnerFilter, search],
  );
  const sort = useTableSort(rows, SHELF_SORT, { key: 'partner' });

  if (error)
    return (
      <div className="text-sm text-red-600" role="alert">
        {error}
      </div>
    );
  if (!data) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{t('shelf.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('shelf.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('stock_dash.search.placeholder')}
            aria-label={t('stock_dash.search.placeholder')}
            className="w-56 rounded border border-border bg-background px-2 py-1"
          />
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">{t('stock_dash.filter.partner')}</span>
            <select
              value={partnerFilter}
              onChange={(event) => setPartnerFilter(event.target.value)}
              className="rounded border border-border bg-background px-2 py-1"
            >
              <option value="">{t('stock_dash.filter.all_partners')}</option>
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.code} — {partner.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>
      {sort.sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('shelf.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <SortableTh label={t('stock_dash.col.partner')} col="partner" sort={sort} />
                <SortableTh label={t('shelf.type')} col="type" sort={sort} />
                <SortableTh label={t('stock_dash.col.code')} col="code" sort={sort} />
                <SortableTh label={t('stock_dash.col.name')} col="name" sort={sort} />
                <th className="w-14 px-3 py-2" aria-label={t('photo.title')} />
                <SortableTh label={t('shelf.qty')} col="qty" sort={sort} align="right" />
                {canMove ? <th className="px-3 py-2">{t('shelf.move.target')}</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sort.sorted.map((row) => (
                <tr key={`${row.partnerId}-${row.skuId}`}>
                  <td className="px-3 py-2">
                    {row.partnerCode} — {row.partnerName}
                  </td>
                  <td className="px-3 py-2">{t(`sku.type.${row.type}`)}</td>
                  <td className="px-3 py-2 font-mono">
                    <Link
                      to={`/partners/${row.partnerId}/skus/${row.skuId}`}
                      className="text-primary hover:underline"
                    >
                      {row.code}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{row.nameLt}</td>
                  <td className="px-3 py-2">
                    <ThumbButton url={row.photoUrl} alt={`${row.code} — ${row.nameLt}`} />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatQty(row.shelfQty)}</td>
                  {canMove ? (
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => setSelected(row)}
                        className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
                      >
                        {t('shelf.move.action')}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selected ? (
        <MoveShelfToPalletDialog
          item={selected}
          onClose={() => setSelected(null)}
          onMoved={reload}
        />
      ) : null}
    </section>
  );
}
